/** Display formatting. No locale is forced — the browser's is used, which is right for a mixed team. */

export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * ⭐⭐ A CALENDAR DATE AND AN INSTANT ARE NOT THE SAME KIND OF THING, AND ONE FUNCTION CANNOT RENDER
 * BOTH. That ambiguity WAS the defect (PH-21), not a detail of it.
 *
 * There used to be a single `formatDate(iso)` here, and it was called with BOTH — `start_date`,
 * `release_date`, `due_on`, `expected_on` (Postgres DATE, sent as `YYYY-MM-DD` text since PH-14's
 * pool-scoped OID 1082 parser) AND `created_at`, `promoted_at` (TIMESTAMPTZ, genuine instants). It
 * did `new Date(iso).toLocaleDateString()`, which is RIGHT for the instants and WRONG for the dates:
 * a bare `YYYY-MM-DD` is parsed as UTC MIDNIGHT, then rendered in local time.
 *
 * 🔴 MEASURED, in `America/New_York`: `formatDate('2026-09-05')` printed `4 Sept 2026`.
 *
 * ⚠️ AND IT IS INVISIBLE FROM HERE. The whole team is UTC+7, where UTC midnight is 07:00 the SAME
 * day and the output happens to be right. Nobody could have seen this by looking — only by forcing
 * the timezone, which is exactly what `format.test.ts` does.
 *
 * ⛔ So there is no `formatDate` any more, deliberately. A caller must say which kind it holds:
 * `formatCalendarDate` for a DATE column, `formatInstantDate`/`formatDateTime` for a TIMESTAMPTZ.
 * A name that REFUSES the wrong input is a control; a comment saying "careful" is decoration.
 */

interface CalendarParts {
  year: number;
  month: number;
  day: number;
}

/**
 * The one definition of "is this a plain calendar date" in this app. STRICT: `2026-09-05` and
 * nothing else — ⛔ not `2026-9-5`, and emphatically not `2026-09-05T00:00:00Z`, because accepting a
 * timestamp here would silently take its UTC date and reintroduce the very off-by-a-day this exists
 * to stop.
 *
 * ⚠️ Range-checked, not calendar-checked: `2026-02-30` parses. Callers that build a Date decide what
 * to do about it — `formatCalendarDate` round-trips and rejects, `splitYmd` in sprint-sections keeps
 * its long-standing behaviour of printing the parts back.
 */
export function parseCalendarDate(value: string): CalendarParts | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (month < 1 || month > 12 || day < 1 || day > 31) return null;
  return { year, month, day };
}

/**
 * A Postgres DATE — a day on a calendar, with no time and no timezone. `5 Sept 2026` in Bangkok,
 * New York, and UTC alike.
 *
 * ⭐ The two halves are what make it timezone-independent, and BOTH are load-bearing: the string is
 * SPLIT rather than parsed (so no local-vs-UTC guess is made on the way in) and it is formatted with
 * an explicit `timeZone: 'UTC'` (so no local offset is applied on the way out). Drop either and the
 * day slips west of Greenwich again. This is the same `Date.UTC`-from-parts move that
 * `occurrence-model.ts` uses for `daysLate`.
 *
 * ⚠️ The LOCALE is still the reader's, which is the point of not hand-rolling month names: a mixed
 * team sees its own convention. `locale` is a parameter only so a test can pin one and assert an
 * exact string — production passes nothing.
 *
 * ⛔ Anything that is not a plain `YYYY-MM-DD` is returned VERBATIM, not blanked. If a deployment
 * ever starts sending timestamps for a DATE column, the reader should see the odd value and say so;
 * an em dash would read as "this ticket has no start date", a claim the data contradicts.
 */
export function formatCalendarDate(iso: string | null | undefined, locale?: string): string {
  if (!iso) return '—';
  const parts = parseCalendarDate(iso);
  if (!parts) return iso;
  const utc = new Date(Date.UTC(parts.year, parts.month - 1, parts.day));
  // Round-trip: rejects 2026-02-30, which Date.UTC would silently roll forward to 2 March.
  if (utc.getUTCMonth() !== parts.month - 1 || utc.getUTCDate() !== parts.day) return iso;
  return utc.toLocaleDateString(locale, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

/**
 * A TIMESTAMPTZ shown as a date only — `created_at`, `promoted_at`. A real instant, so it MUST be
 * rendered in the reader's local time: the moment a row was created is genuinely a different day for
 * a reader in Bangkok than for one in New York, and that is correct rather than a bug.
 *
 * ⛔ Do not "fix" this the way `formatCalendarDate` is fixed. Pinning this to UTC would print
 * yesterday's date for anything created after 07:00 Bangkok — which is most of the working day.
 */
export function formatInstantDate(iso: string | null | undefined, locale?: string): string {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString(locale, { day: 'numeric', month: 'short', year: 'numeric' });
}

export function formatClock(date: Date): string {
  return date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

export function formatRelative(iso: string | null | undefined): string {
  if (!iso) return '—';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '—';
  const seconds = Math.round((Date.now() - then) / 1000);
  const units: [Intl.RelativeTimeFormatUnit, number][] = [
    ['second', 60],
    ['minute', 60],
    ['hour', 24],
    ['day', 7],
    ['week', 4.35],
    ['month', 12],
    ['year', Number.POSITIVE_INFINITY],
  ];
  const formatter = new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' });
  let value = seconds;
  for (const [unit, span] of units) {
    if (Math.abs(value) < span) return formatter.format(-Math.round(value), unit);
    value /= span;
  }
  return formatter.format(-Math.round(value), 'year');
}

/** ⚠️ NUMERIC(5,2) arrives from the API as a STRING (`"10.00"`), so this must accept both. */
export function formatPoints(points: number | string | null | undefined): string | null {
  if (points === null || points === undefined || points === '') return null;
  const numeric = Number(points);
  if (!Number.isFinite(numeric)) return null;
  return Number.isInteger(numeric) ? String(numeric) : numeric.toFixed(1);
}

/**
 * 🔴 `fix_versions` CHANGED SHAPE UNDER US. This app was reconciled against the API at `ab8f652`,
 * where the list endpoint sent no versions at all and this client typed the future field as
 * `{id, name}[]`. The API then added it at `680ab8b` as a plain `text[]` of NAMES —
 * `array_agg(fver.name)` in `TICKET_SELECT`.
 *
 * ⚠️ The failure that combination produces is the quiet kind: `versions.map(v => v.name)` on an array
 * of strings prints `undefined, undefined` in small grey type on every row, which reads as bad DATA
 * rather than as a client reading the wrong shape. Both shapes are accepted here, in one place.
 *
 * ⛔ It does not invent a name for a shape it does not recognise — an unusable entry is dropped, and
 * an empty result renders as `None`, which is what "returned, but none" means.
 */
export function versionNames(versions: readonly unknown[]): string[] {
  return versions.flatMap((entry) => {
    if (typeof entry === 'string') return entry.trim() ? [entry] : [];
    if (entry && typeof entry === 'object' && 'name' in entry) {
      const name = (entry as { name: unknown }).name;
      return typeof name === 'string' && name.trim() ? [name] : [];
    }
    return [];
  });
}

export function initialsOf(name: string | null | undefined): string {
  const source = (name ?? '').trim();
  if (!source) return '?';
  const parts = source.split(/[\s._-]+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return (parts[0] as string).slice(0, 2).toUpperCase();
  return `${(parts[0] as string)[0]}${(parts[1] as string)[0]}`.toUpperCase();
}

/** Deterministic avatar tint so the same person is the same colour on every row. */
export function avatarTint(seed: string | null | undefined): string {
  const palette = ['#5243aa', '#0055cc', '#206a83', '#216e4e', '#a54800', '#ae2e24', '#943d73'];
  const source = seed ?? '';
  let hash = 0;
  for (let i = 0; i < source.length; i += 1) {
    hash = (hash * 31 + source.charCodeAt(i)) >>> 0;
  }
  return palette[hash % palette.length] as string;
}
